import { useEffect, useState } from 'react'
import { Accordion, Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { useAuth } from '../../../../modules/auth'
import { parseId } from '../../../helpers'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { useGetUser } from '../../../store/userStore'
import { IAllUserNode } from '../../../types/users'
import {
  ChannelConfigData,
  TelephonyProviderValue,
  VicidialCampaignOption,
  fetchVicidialCampaignOptions,
  useChannelConfig,
  useUserChannelConfig,
} from '../../channelConfig/hooks/useChannelConfig'

interface Props {
  node: IAllUserNode
}

type UserTelephonyForm = Pick<
  ChannelConfigData,
  | 'telephony_provider'
  | 'whatsapp_provider'
  | 'twilio_whatsapp_number'
  | 'twilio_phone_number'
  | 'twilio_account_sid'
  | 'twilio_auth_token'
  | 'meta_whatsapp_phone_number_id'
  | 'botmaker_phone_line_number'
  | 'vicidial_enabled'
  | 'vicidial_agent_user'
  | 'vicidial_agent_password'
  | 'vicidial_phone_login'
  | 'vicidial_phone_password'
  | 'vicidial_campaign_id'
>

const EMPTY_USER_TELEPHONY_FORM: UserTelephonyForm = {
  telephony_provider: '',
  whatsapp_provider: 'NONE',
  twilio_whatsapp_number: '',
  twilio_phone_number: '',
  twilio_account_sid: '',
  twilio_auth_token: '',
  meta_whatsapp_phone_number_id: '',
  botmaker_phone_line_number: '',
  vicidial_enabled: false,
  vicidial_agent_user: '',
  vicidial_agent_password: '',
  vicidial_phone_login: '',
  vicidial_phone_password: '',
  vicidial_campaign_id: '',
}

const buildUserTelephonyForm = (config?: Partial<ChannelConfigData>): UserTelephonyForm => ({
  telephony_provider: (config?.telephony_provider as TelephonyProviderValue) || '',
  whatsapp_provider: (config?.whatsapp_provider as any) || 'NONE',
  twilio_whatsapp_number: config?.twilio_whatsapp_number || '',
  twilio_phone_number: config?.twilio_phone_number || '',
  twilio_account_sid: config?.twilio_account_sid || '',
  twilio_auth_token: config?.twilio_auth_token || '',
  meta_whatsapp_phone_number_id: config?.meta_whatsapp_phone_number_id || '',
  botmaker_phone_line_number: config?.botmaker_phone_line_number || '',
  vicidial_enabled: Boolean(config?.vicidial_enabled),
  vicidial_agent_user: config?.vicidial_agent_user || '',
  vicidial_agent_password: config?.vicidial_agent_password || '',
  vicidial_phone_login: config?.vicidial_phone_login || '',
  vicidial_phone_password: config?.vicidial_phone_password || '',
  vicidial_campaign_id: config?.vicidial_campaign_id || '',
})

export const UserChannelConfigTab: React.FC<Props> = ({ node }) => {
  const { auth } = useAuth()
  const orgId = node.organization?.id ? `${parseId(node.organization.id)}` : undefined
  const userId = node.id ? `${parseId(node.id)}` : undefined

  const {
    config: orgConfig,
    loading: loadingOrgConfig,
  } = useChannelConfig(orgId)
  const {
    config: userConfig,
    loading: loadingUserConfig,
    saving: savingUserConfig,
    saveUserConfig,
  } = useUserChannelConfig(userId)

  const [orgForm, setOrgForm] = useState<Partial<ChannelConfigData>>({})
  const [userForm, setUserForm] = useState<UserTelephonyForm>(EMPTY_USER_TELEPHONY_FORM)
  const [campaignOptions, setCampaignOptions] = useState<VicidialCampaignOption[]>([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  // const [isOrgDirty, setIsOrgDirty] = useState(false)
  const [isUserDirty, setIsUserDirty] = useState(false)

  const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const isAdmin = userGroups.includes('Administrador') || isSuperAdmin

  const getToken = () => auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)

  useEffect(() => {
    if (!loadingOrgConfig) {
      setOrgForm(orgConfig)
      // setIsOrgDirty(false)
    }
  }, [orgConfig, loadingOrgConfig])

  useEffect(() => {
    if (!loadingUserConfig) {
      setUserForm(buildUserTelephonyForm(userConfig))
      setIsUserDirty(false)
    }
  }, [userConfig, loadingUserConfig])

  useEffect(() => {
    let cancelled = false

    const loadCampaigns = async () => {
      const token = getToken()
      if (
        !token ||
        !orgConfig.vicidial_base_url ||
        !orgConfig.vicidial_api_user ||
        !orgConfig.vicidial_api_password
      ) {
        setCampaignOptions([])
        return
      }

      setLoadingCampaigns(true)
      try {
        const campaigns = await fetchVicidialCampaignOptions({
          token,
          baseUrl: orgConfig.vicidial_base_url,
          apiUser: orgConfig.vicidial_api_user,
          apiPassword: orgConfig.vicidial_api_password,
        })
        if (!cancelled) {
          setCampaignOptions(campaigns)
        }
      } catch {
        if (!cancelled) {
          setCampaignOptions([])
        }
      } finally {
        if (!cancelled) {
          setLoadingCampaigns(false)
        }
      }
    }

    loadCampaigns()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orgConfig.vicidial_base_url,
    orgConfig.vicidial_api_user,
    orgConfig.vicidial_api_password,
  ])

  // const handleOrgChange = (field: keyof ChannelConfigData, value: any) => {
  //   setOrgForm((prev) => ({ ...prev, [field]: value }))
  //   setIsOrgDirty(true)
  // }

  const handleUserChange = (field: keyof UserTelephonyForm, value: string | boolean) => {
    setUserForm((prev) => ({ ...prev, [field]: value }))
    setIsUserDirty(true)
  }

  // const handleSaveOrgConfig = async () => {
  //   const success = await saveOrgConfig(orgForm)
  //   if (success) {
  //     toast.success('Organization channels saved successfully')
  //     setIsOrgDirty(false)
  //     return
  //   }
  //   toast.error('Error saving organization channels')
  // }

  const handleSaveUserConfig = async () => {
    const success = await saveUserConfig(userForm)
    if (success) {
      toast.success('User VICIdial mapping saved successfully')
      setIsUserDirty(false)
      return
    }
    toast.error('Error saving user VICIdial mapping')
  }

  const handleResetToInherited = () => {
    setUserForm(EMPTY_USER_TELEPHONY_FORM)
    setIsUserDirty(true)
  }

  if (!isAdmin) {
    return (
      <Card className='mb-5'>
        <Card.Body className='text-center py-10'>
          <i className='bi bi-lock-fill fs-1 text-muted mb-3 d-block' />
          <h5 className='text-gray-700'>Access Restricted</h5>
          <p className='text-muted mb-0'>
            Only administrators can manage channel and VICIdial settings.
          </p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <div className='d-flex flex-column gap-5'>

      {/* ══════════════════════════════════════════════════════════
          CARD 1 — Personal Settings (user-level overrides)
          ══════════════════════════════════════════════════════════ */}
      <Card className='mb-0'>
        <Card.Body className='p-5'>
          <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 mb-5'>
            <div>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <h3 className='fw-bold text-gray-900 mb-0'>
                  <i className='bi bi-person-gear me-2 text-primary' />
                  Personal Settings
                </h3>
                <Badge bg='primary' className='ms-1'>
                  {node.firstName} {node.lastName}
                </Badge>
              </div>
              <p className='text-muted fs-7 mb-0'>
                Overrides exclusive to this agent. These take priority over organization defaults.
              </p>
            </div>
            <Button
              variant='primary'
              onClick={handleSaveUserConfig}
              disabled={savingUserConfig || !isUserDirty || !userId}
            >
              {savingUserConfig ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Saving...
                </>
              ) : (
                <>
                  <i className='bi bi-check-lg me-1' />
                  Save Personal Settings
                </>
              )}
            </Button>
          </div>

          {!userId ? (
            <Alert variant='warning' className='mb-0'>
              Save the user first before configuring personal settings.
            </Alert>
          ) : loadingUserConfig ? (
            <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 180 }}>
              <Spinner animation='border' variant='primary' />
            </div>
          ) : (
            <Accordion alwaysOpen>

              {/* ─── Personal WhatsApp Number ─── */}
              <Accordion.Item eventKey='whatsapp-override'>
                <Accordion.Header>
                  <div className='d-flex align-items-center gap-2'>
                    <i className='bi bi-whatsapp fs-4 text-success' />
                    <span className='fw-bold fs-5'>Personal WhatsApp Number</span>
                    <Badge bg={userForm.whatsapp_provider && userForm.whatsapp_provider !== 'NONE' ? 'success' : 'secondary'}>
                      {userForm.whatsapp_provider && userForm.whatsapp_provider !== 'NONE' ? userForm.whatsapp_provider : 'Not configured'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <p className='text-muted fs-7 mb-4'>
                    Assign a direct phone number to this agent. Incoming messages to this number will be routed exclusively to them, bypassing the organization's round-robin.
                  </p>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <Form.Group>
                        <Form.Label>Provider</Form.Label>
                        <Form.Select
                          value={userForm.whatsapp_provider || 'NONE'}
                          onChange={(e) => handleUserChange('whatsapp_provider', e.target.value)}
                        >
                          <option value='NONE'>Disabled (inherit organization)</option>
                          <option value='TWILIO'>Twilio</option>
                          <option value='BOTMAKER'>Botmaker</option>
                          <option value='META'>Meta Cloud API</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                    {userForm.whatsapp_provider === 'TWILIO' && (
                      <>
                        <div className='col-md-4'>
                          <Form.Group>
                            <Form.Label>Twilio WhatsApp Number</Form.Label>
                            <Form.Control
                              type='text'
                              value={userForm.twilio_whatsapp_number || ''}
                              onChange={(e) => handleUserChange('twilio_whatsapp_number', e.target.value)}
                              placeholder='+1234567890'
                            />
                            <Form.Text className='text-muted'>Requires + prefix.</Form.Text>
                          </Form.Group>
                        </div>
                        <div className='col-md-4'>
                          <Form.Group>
                            <Form.Label>Account SID (Optional)</Form.Label>
                            <Form.Control
                              type='text'
                              value={userForm.twilio_account_sid || ''}
                              onChange={(e) => handleUserChange('twilio_account_sid', e.target.value)}
                              placeholder='AC...'
                            />
                            <Form.Text className='text-muted'>Override org SID if subaccount.</Form.Text>
                          </Form.Group>
                        </div>
                        <div className='col-md-4'>
                          <Form.Group>
                            <Form.Label>Auth Token (Optional)</Form.Label>
                            <Form.Control
                              type='password'
                              autoComplete="new-password"
                              value={userForm.twilio_auth_token || ''}
                              onChange={(e) => handleUserChange('twilio_auth_token', e.target.value)}
                            />
                            <Form.Text className='text-muted'>Override org token.</Form.Text>
                          </Form.Group>
                        </div>
                      </>
                    )}
                    {userForm.whatsapp_provider === 'BOTMAKER' && (
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Botmaker Phone Line</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.botmaker_phone_line_number || ''}
                            onChange={(e) => handleUserChange('botmaker_phone_line_number', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                    )}
                    {userForm.whatsapp_provider === 'META' && (
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Meta Phone Number ID</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.meta_whatsapp_phone_number_id || ''}
                            onChange={(e) => handleUserChange('meta_whatsapp_phone_number_id', e.target.value)}
                            placeholder='1234567890'
                          />
                        </Form.Group>
                      </div>
                    )}
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              {/* ─── VICIdial Settings ─── */}
              <Accordion.Item eventKey='vicidial-mapping' className='mt-3'>
                <Accordion.Header>
                  <div className='d-flex align-items-center gap-2'>
                    <img src='/media/tfi/vicidial-icon.svg' alt='VICIdial' style={{ width: '24px', height: '24px' }} />
                    <span className='fw-bold fs-5'>VICIdial Settings</span>
                    <Badge bg={userForm.vicidial_enabled ? 'success' : 'secondary'}>
                      {userForm.vicidial_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className='d-flex justify-content-between align-items-start gap-3 mb-5'>
                    <p className='text-muted fs-7 mb-0'>
                      Per-agent telephony override. Leave empty to inherit the organization configuration.
                    </p>
                    <Button variant='light' size='sm' onClick={handleResetToInherited} disabled={savingUserConfig}>
                      Reset to Inherited
                    </Button>
                  </div>
                  <div className='mt-2'>
                    <h6 className='fw-bold text-primary mb-3'><i className='bi bi-sliders me-2' />Override Mode</h6>
                    <div className='row g-4 mb-5'>
                      <div className='col-md-4'>
                        <Form.Group>
                          <Form.Label>Telephony provider</Form.Label>
                          <Form.Select
                            value={userForm.telephony_provider}
                            onChange={(e) =>
                              handleUserChange('telephony_provider', e.target.value as TelephonyProviderValue)
                            }
                          >
                            <option value=''>Inherit organization</option>
                            <option value='RINGCENTRAL'>RingCentral</option>
                            <option value='VICIDIAL'>VICIdial</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <div className='col-md-4'>
                        <Form.Group className='h-100 d-flex align-items-end'>
                          <Form.Check
                            type='switch'
                            id='user_vicidial_enabled'
                            label='Enable VICIdial override for this user'
                            checked={userForm.vicidial_enabled}
                            onChange={(e) => handleUserChange('vicidial_enabled', e.target.checked)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-4'>
                        <div className='alert alert-light-primary border border-primary border-dashed mb-0 h-100'>
                          <div className='fw-bold mb-1'>Current mode</div>
                          <div className='text-muted fs-7'>
                            {userForm.telephony_provider
                              ? `This user will explicitly use ${userForm.telephony_provider}.`
                              : 'This user will inherit the provider selected at organization level.'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <h6 className='fw-bold text-info mb-3'><i className='bi bi-person-badge me-2' />Agent Credentials</h6>
                    <div className='row g-4 mb-5'>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>VICIdial agent user</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.vicidial_agent_user}
                            onChange={(e) => handleUserChange('vicidial_agent_user', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Agent password</Form.Label>
                          <Form.Control
                            type='password'
                            autoComplete='new-password'
                            value={userForm.vicidial_agent_password}
                            onChange={(e) => handleUserChange('vicidial_agent_password', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Extension / phone login</Form.Label>
                          <Form.Control
                            type='text'
                            value={userForm.vicidial_phone_login}
                            onChange={(e) => handleUserChange('vicidial_phone_login', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Phone password</Form.Label>
                          <Form.Control
                            type='password'
                            autoComplete='new-password'
                            value={userForm.vicidial_phone_password}
                            onChange={(e) => handleUserChange('vicidial_phone_password', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                    </div>

                    <div className='d-flex align-items-center gap-2 mb-3'>
                      <h6 className='fw-bold text-success mb-0'><i className='bi bi-diagram-3 me-2' />Campaign Routing</h6>
                      {loadingCampaigns && (
                        <span className='text-primary fs-8 d-flex align-items-center gap-1'>
                          <Spinner animation='border' size='sm' />
                          Loading...
                        </span>
                      )}
                    </div>
                    <div className='row g-4'>
                      <div className='col-md-6'>
                        <Form.Group>
                          <Form.Label>Campaign</Form.Label>
                          {campaignOptions.length > 0 ? (
                            <Form.Select
                              value={userForm.vicidial_campaign_id}
                              onChange={(e) => handleUserChange('vicidial_campaign_id', e.target.value)}
                            >
                              <option value=''>
                                Inherit organization
                                {orgConfig.vicidial_campaign_id
                                  ? ` (${orgConfig.vicidial_campaign_id})`
                                  : ' (no default campaign)'}
                              </option>
                              {campaignOptions.map((campaign) => (
                                <option key={campaign.campaign_id} value={campaign.campaign_id}>
                                  {campaign.campaign_id} - {campaign.campaign_name}
                                  {campaign.active === 'Y' ? '' : ' (Inactive)'}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              type='text'
                              placeholder={orgConfig.vicidial_campaign_id || 'Inherit organization campaign'}
                              value={userForm.vicidial_campaign_id}
                              onChange={(e) => handleUserChange('vicidial_campaign_id', e.target.value)}
                            />
                          )}
                          <Form.Text className='text-muted'>
                            Leave it blank to inherit the organization campaign.
                          </Form.Text>
                        </Form.Group>
                      </div>
                      <div className='col-md-6'>
                        <div className='alert alert-light-success border border-success border-dashed mb-0 h-100'>
                          <div className='fw-bold mb-1'>Routing behavior</div>
                          <div className='text-muted fs-7'>
                            Only set a campaign here when this agent should work on a different VICIdial queue.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

            </Accordion>
          )}
        </Card.Body>
      </Card>

      {/* ══════════════════════════════════════════════════════════
          CARD 2 — Organization Settings (shared, org-level)
          ══════════════════════════════════════════════════════════ */}
      <Card className='mb-0'>
        <Card.Body className='p-5'>
          <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 mb-5'>
            <div>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <h3 className='fw-bold text-gray-900 mb-0'>
                  <i className='bi bi-broadcast me-2' />
                  Organization Channels
                </h3>
                <Badge bg='light' text='dark' className='border'>
                  {node.organization?.name || 'No organization'}
                </Badge>
                <Badge bg='warning' text='dark'>Shared</Badge>
              </div>
              <p className='text-muted fs-7 mb-0'>
                These settings apply to all agents in the organization.
              </p>
            </div>
          </div>
          {!orgId ? (
            <Card className='mb-0'>
              <Card.Body className='text-center py-10'>
                <i className='bi bi-exclamation-triangle fs-1 text-warning mb-3 d-block' />
                <h5 className='text-gray-700'>No Organization Assigned</h5>
                <p className='text-muted mb-0'>
                  This user must be assigned to an organization before editing SMS, Botmaker or
                  Email settings.
                </p>
              </Card.Body>
            </Card>
          ) : loadingOrgConfig ? (
            <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 280 }}>
              <Spinner animation='border' variant='primary' />
            </div>
          ) : (
            <>
              <Accordion alwaysOpen>
                <Accordion.Item eventKey='twilio'>
                  <Accordion.Header>
                    <div className='d-flex align-items-center gap-2'>
                      <i className='bi bi-chat-dots fs-4 text-info' />
                      <span className='fw-bold fs-5'>Twilio SMS</span>
                      <Badge bg={orgForm.twilio_enabled ? 'success' : 'secondary'}>
                        {orgForm.twilio_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className='row g-4'>
                      <div className='col-12'>
                        <div className='d-flex align-items-center gap-3 p-3 bg-light rounded border'>
                          <i className='bi bi-telephone fs-4 text-gray-500'></i>
                          <div>
                            <div className='text-muted fs-7 mb-1'>Phone Number</div>
                            <div className='fw-bold fs-5 text-gray-900'>{orgForm.twilio_phone_number || 'Not configured'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

                {/* ─── WhatsApp Integration (Multi-Provider) ─── */}
                <Accordion.Item eventKey="whatsapp" className="mt-3">
                  <Accordion.Header>
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-whatsapp fs-4 text-success"></i>
                      <span className="fw-bold fs-5">WhatsApp Integration</span>
                      <Badge bg={orgForm.whatsapp_provider && orgForm.whatsapp_provider !== 'NONE' ? 'success' : 'secondary'} className="ms-2">
                        {orgForm.whatsapp_provider && orgForm.whatsapp_provider !== 'NONE' ? orgForm.whatsapp_provider : 'Disabled'}
                      </Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="row g-4">
                      <div className="col-12">
                        <div className="d-flex align-items-center gap-3 p-3 bg-light rounded border mb-3">
                          <i className="bi bi-diagram-2 fs-4 text-gray-500"></i>
                          <div>
                            <div className="text-muted fs-7 mb-1">Provider</div>
                            <div className="fw-bold fs-5 text-gray-900">
                              {orgForm.whatsapp_provider && orgForm.whatsapp_provider !== 'NONE'
                                ? orgForm.whatsapp_provider
                                : 'Disabled'}
                            </div>
                          </div>
                        </div>

                        {orgForm.whatsapp_provider === 'TWILIO' && (
                          <div className="d-flex align-items-center gap-3 p-3 bg-light-primary rounded border border-primary border-dashed">
                            <i className="bi bi-telephone text-primary fs-4"></i>
                            <div>
                              <div className="text-primary opacity-75 fs-7 mb-1">Twilio WhatsApp Number</div>
                              <div className="fw-bold fs-5 text-primary-dark">
                                {orgForm.twilio_whatsapp_number || orgForm.twilio_phone_number || 'Not configured'}
                              </div>
                            </div>
                          </div>
                        )}

                        {orgForm.whatsapp_provider === 'BOTMAKER' && (
                          <div className="d-flex align-items-center gap-3 p-3 bg-light-success rounded border border-success border-dashed">
                            <i className="bi bi-telephone text-success fs-4"></i>
                            <div>
                              <div className="text-success opacity-75 fs-7 mb-1">Botmaker Phone Line</div>
                              <div className="fw-bold fs-5 text-success-dark">
                                {orgForm.botmaker_phone_line_number || 'Not configured'}
                              </div>
                            </div>
                          </div>
                        )}

                        {orgForm.whatsapp_provider === 'META' && (
                          <div className="d-flex align-items-center gap-3 p-3 bg-light-info rounded border border-info border-dashed">
                            <i className="bi bi-telephone text-info fs-4"></i>
                            <div>
                              <div className="text-info opacity-75 fs-7 mb-1">Meta Display Number</div>
                              <div className="fw-bold fs-5 text-info-dark">
                                {orgForm.meta_whatsapp_phone_display || 'Not configured'}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey='email' className='mt-3'>
                  <Accordion.Header>
                    <div className='d-flex align-items-center gap-2'>
                      <i className='bi bi-envelope fs-4 text-warning' />
                      <span className='fw-bold fs-5'>Email SMTP</span>
                      <Badge bg={orgForm.email_enabled ? 'success' : 'secondary'}>
                        {orgForm.email_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className='row g-4'>
                      <div className='col-12'>
                        {orgForm.email_enabled ? (
                          <div className='alert alert-success d-flex align-items-center mb-0'>
                            <i className='bi bi-check-circle-fill fs-4 me-3'></i>
                            <div>
                              <strong>Email Services are Active</strong>
                              <div className='fs-7 opacity-75'>
                                The organization is currently configured to send outgoing emails.
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className='alert alert-secondary d-flex align-items-center mb-0'>
                            <i className='bi bi-x-circle-fill fs-4 me-3'></i>
                            <div>
                              <strong>Email Services are Disabled</strong>
                              <div className='fs-7 opacity-75'>
                                No outgoing email services are configured for this organization.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

              </Accordion>

              {/* Webhook Info Card */}
              {orgConfig.webhook_token && (
                <Card className="mt-5 border-dashed border-primary bg-light-primary">
                  <Card.Body>
                    <div className="d-flex align-items-start gap-3">
                      <i className="bi bi-link-45deg fs-1 text-primary"></i>
                      <div className="w-100">
                        <h5 className="fw-bold text-gray-900 mb-2">Webhook URLs</h5>
                        <p className="text-muted mb-3 fs-7">
                          Use these URLs to configure your external services (Botmaker & Twilio).
                        </p>

                        {/* Botmaker Webhook */}
                        <div className="bg-white rounded p-3 mb-3 border border-gray-200">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <small className="fw-bold text-gray-800">Botmaker Webhook</small>
                            <Badge bg="light" text="dark" className="border">Standard</Badge>
                          </div>
                          <code className="text-primary fs-7 d-block text-break">
                            {window.location.origin.replace('3000', '8000')}/webhooks/botmaker/{orgConfig.webhook_token}/
                          </code>
                        </div>

                        {/* Twilio Webhook */}
                        <div className="bg-white rounded p-3 border border-gray-200">
                          <WebhookUrlDisplay
                            baseUrl={window.location.origin.replace('3000', '8000')}
                            token={userConfig?.webhook_token || orgConfig.webhook_token}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Meta Webhook Info Card */}
              {orgConfig.webhook_token && orgForm.whatsapp_provider === 'META' && (
                <Card className="mt-4 border-dashed border-success bg-light-success">
                  <Card.Body>
                    <div className="d-flex align-items-start gap-3">
                      <i className="bi bi-meta fs-1 text-success"></i>
                      <div className="w-100">
                        <h5 className="fw-bold text-gray-900 mb-2">Meta Webhook Configuration</h5>
                        <p className="text-muted mb-3 fs-7">
                          Configure this URL in your Meta App Dashboard under WhatsApp Webhooks. Use the Verify Token from the configuration above.
                        </p>
                        <div className="bg-white rounded p-3 border border-gray-200">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <small className="fw-bold text-gray-800">Callback URL</small>
                          </div>
                          <code className="text-success fs-7 d-block text-break">
                            {window.location.origin.replace('3000', '8000')}/webhooks/meta-whatsapp/meta-whatsapp/
                          </code>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

const WebhookUrlDisplay = ({ baseUrl, token }: { baseUrl: string, token: string }) => {
  const [mode, setMode] = useState<'standard' | 'messaging_service'>('messaging_service')

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <small className="fw-bold text-gray-800">Twilio Webhook</small>
        <div className="d-flex bg-light rounded p-1 border">
          <button
            className={`btn btn-sm py-1 px-3 border-0 ${mode === 'messaging_service' ? 'bg-white shadow-sm fw-bold text-primary' : 'text-gray-600'}`}
            onClick={() => setMode('messaging_service')}
            style={{ fontSize: '0.75rem', borderRadius: '4px' }}
          >
            Messaging Service
          </button>
          <button
            className={`btn btn-sm py-1 px-3 border-0 ${mode === 'standard' ? 'bg-white shadow-sm fw-bold text-primary' : 'text-gray-600'}`}
            onClick={() => setMode('standard')}
            style={{ fontSize: '0.75rem', borderRadius: '4px' }}
          >
            Standard
          </button>
        </div>
      </div>

      <code className="text-primary fs-7 d-block text-break mb-2">
        {mode === 'messaging_service'
          ? `${baseUrl}/webhooks/twilio/`
          : `${baseUrl}/webhooks/twilio/${token}/`
        }
      </code>

      {mode === 'messaging_service' && (
        <div className="alert alert-info d-flex align-items-center p-2 mb-0 mt-2" style={{ fontSize: '0.75rem' }}>
          <i className="bi bi-info-circle-fill me-2 fs-6"></i>
          <div>
            <strong>Routing by Phone Number:</strong> Configures this URL in your Twilio Messaging Service.
            Incoming messages will be routed to this organization based on the matched <strong>To</strong> number.
          </div>
        </div>
      )}
      {mode === 'standard' && (
        <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
          <i className="bi bi-arrow-return-right me-1"></i>
          Use this URL to configure a specific phone number directly (bypassing Messaging Service).
        </div>
      )}
    </div>
  )
}
